<template>
  <div class="account-details">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        variant="text"
        class="mr-2"
        :to="{ name: 'accounts' }"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h4">{{ t('accounts.details') }}</h1>
    </div>
    
    <v-skeleton-loader
      v-if="carregando"
      type="card"
    ></v-skeleton-loader>
    
    <template v-else-if="conta">
      <!-- Painel de Informações da Conta -->
      <v-card class="mb-4">
        <v-card-title>
          <v-icon color="primary" class="mr-2">mdi-account-outline</v-icon>
          Informações da Conta
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <div class="d-flex align-center mb-4">
            <v-avatar color="primary" size="64" class="mr-4">
              <span class="text-h5 font-weight-bold">{{ formatEndereco(address) }}</span>
            </v-avatar>
            
            <div class="flex-grow-1">
              <div class="d-flex align-center">
                <span class="text-truncate" style="max-width: 400px;">{{ address }}</span>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="copiarEndereco"
                  class="ml-2"
                >
                  <v-icon size="small">{{ enderecoCopiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                </v-btn>
              </div>
              
              <div class="mt-1 text-caption">
                {{ conta.isContrato ? 'Contrato Inteligente' : 'Conta Padrão' }}
                <v-chip v-if="conta.isValidador" class="ml-2" size="x-small" color="success">{{ t('blocks.validator') }}</v-chip>
              </div>
            </div>
            
            <v-btn
              v-if="!conta.isContrato"
              variant="outlined"
              color="primary"
              class="ml-4"
              prepend-icon="mdi-send"
              :to="{ name: 'transactions' }"
            >
              Enviar {{ coin }}
            </v-btn>
          </div>
          
          <v-row>
            <v-col cols="12" md="4">
              <v-card variant="outlined" class="text-center pa-4">
                <div class="text-overline">Saldo {{ coin }}</div>
                <div class="text-h4 font-weight-medium">{{ formatarValorToken(conta.saldo) }}</div>
                <div v-if="valorFiatKinX" class="text-caption">≈ {{ valorFiatKinX }}</div>
              </v-card>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-card variant="outlined" class="text-center pa-4">
                <div class="text-overline">{{ t('tx.title') }}</div>
                <div class="text-h4 font-weight-medium">{{ formatarNumeroGrande(conta.numTransacoes) }}</div>
                <div class="text-caption">{{ t('accounts.totalTx') }}</div>
              </v-card>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-card variant="outlined" class="text-center pa-4">
                <div class="text-overline">Tokens</div>
                <div class="text-h4 font-weight-medium">{{ conta.tokens.length || 0 }}</div>
                <div class="text-caption">{{ t('accounts.tokensQrc20') }}</div>
              </v-card>
            </v-col>
          </v-row>
          
          <div v-if="conta.isValidador" class="mt-4">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-subtitle-1 mb-3">{{ t('validators.details') }}</h3>
            
            <v-row>
              <v-col cols="12" md="4">
                <InfoItem :label="t('validators.stake')" :valor="formatarValorToken(conta.stake)" destacar />
              </v-col>
              
              <v-col cols="12" md="4">
                <InfoItem :label="t('validators.merit')" :valor="`${conta.merit}/100`" destacar />
              </v-col>
              
              <v-col cols="12" md="4">
                <InfoItem :label="t('validators.posemScore')" :valor="`${conta.posemScore}/100`" destacar />
              </v-col>
              
              <v-col cols="12" md="4">
                <InfoItem label="Blocos Propostos" :valor="formatarNumeroGrande(conta.blocosPropostos)" />
              </v-col>
              
              <v-col cols="12" md="4">
                <InfoItem :label="t('validators.uptime')" :valor="`${conta.uptime.toFixed(2)}%`" />
              </v-col>
              
              <v-col cols="12" md="4">
                <InfoItem label="Último Bloco" :valor="formatarData(conta.ultimoBloco)" />
              </v-col>
            </v-row>
          </div>
          
          <div v-if="conta.isContrato" class="mt-4">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-subtitle-1 mb-3">{{ t('contracts.title') }}</h3>
            
            <v-row>
              <v-col cols="12" md="6">
                <InfoItem label="Criador" :valor="conta.criador" copiavel endereco />
              </v-col>
              
              <v-col cols="12" md="6">
                <InfoItem label="Criado em" :valor="formatarData(conta.criadoEm)" />
              </v-col>
              
              <v-col cols="12" md="12">
                <InfoItem label="Hash da Transação" :valor="conta.txHash" copiavel />
              </v-col>
            </v-row>
            
            <v-btn
              variant="outlined"
              color="primary"
              class="mt-4"
              prepend-icon="mdi-code-braces"
              :to="{ name: 'developer-ide' }"
            >
              Ver Código Fonte
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Tokens -->
      <v-card v-if="conta.tokens && conta.tokens.length > 0" class="mb-4">
        <v-card-title>
          <v-icon color="primary" class="mr-2">mdi-currency-usd</v-icon>
          Tokens QRC20
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="pa-0">
          <v-data-table
            :headers="cabecalhosTokens"
            :items="conta.tokens"
            hover
            density="comfortable"
          >
            <!-- Token -->
            <template v-slot:item.token="{ item }">
              <div class="d-flex align-center">
                <v-avatar color="primary" size="32" class="mr-2">
                  <span class="caption">{{ item.simbolo.charAt(0) }}</span>
                </v-avatar>
                <div>
                  <div>{{ item.nome }}</div>
                  <div class="text-caption text-grey">{{ item.simbolo }}</div>
                </div>
              </div>
            </template>
            
            <!-- Saldo -->
            <template v-slot:item.saldo="{ item }">
              <span class="font-weight-medium">
                {{ formatarValorToken(item.saldo, item.simbolo, 4) }}
              </span>
            </template>
            
            <!-- Valor -->
            <template v-slot:item.valor="{ item }">
              <span v-if="item.valorFiat">{{ item.valorFiat }}</span>
              <span v-else>-</span>
            </template>
            
            <!-- Contrato -->
            <template v-slot:item.contrato="{ item }">
              <router-link 
                :to="{ name: 'account-details', params: { address: item.contrato } }"
                class="text-decoration-none"
              >
                <div class="text-truncate" style="max-width: 120px;">
                  {{ formatarHash(item.contrato) }}
                </div>
              </router-link>
            </template>
            
            <!-- Ações -->
            <template v-slot:item.acoes="{ item }">
              <div class="d-flex">
                <v-btn icon size="small" variant="text" :to="{ name: 'dex', query: { symbol: item.simbolo } }" class="mx-1">
                  <v-icon>mdi-swap-horizontal</v-icon>
                  <v-tooltip activator="parent" location="top">{{ t('dex.swap') }}</v-tooltip>
                </v-btn>
                
                <v-btn icon size="small" variant="text" :to="{ name: 'account-details', params: { address: item.contrato } }" class="mx-1">
                  <v-icon>mdi-information-outline</v-icon>
                  <v-tooltip activator="parent" location="top">{{ t('dev.tokenDetails') }}</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
      
      <!-- Histórico de Transações -->
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <div>
            <v-icon color="primary" class="mr-2">mdi-swap-horizontal</v-icon>
            Transações
          </div>
          
          <v-tabs
            v-model="tabTransacoes"
            color="primary"
            grow
          >
            <v-tab value="todas">{{ t('app.all') }}</v-tab>
            <v-tab value="enviadas">{{ t('tx.sent') }}</v-tab>
            <v-tab value="recebidas">{{ t('tx.received') }}</v-tab>
          </v-tabs>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="pa-0">
          <v-window v-model="tabTransacoes">
            <v-window-item value="todas">
              <TransacoesList
                :titulo="null"
                :transacoes="transacoesFiltradas"
                :carregando="carregandoTransacoes"
                :mostrar-botao-ver-mais="false"
                :mostrar-colunas="['hash', 'blockNumber', 'timestamp', 'from', 'direction', 'to', 'value', 'status']"
                :items-por-pagina="10"
                :esconder-paginacao="false"
              />
            </v-window-item>
            
            <v-window-item value="enviadas">
              <TransacoesList
                :titulo="null"
                :transacoes="transacoesEnviadas"
                :carregando="carregandoTransacoes"
                :mostrar-botao-ver-mais="false"
                :mostrar-colunas="['hash', 'blockNumber', 'timestamp', 'direction', 'to', 'value', 'status']"
                :items-por-pagina="10"
                :esconder-paginacao="false"
              />
            </v-window-item>
            
            <v-window-item value="recebidas">
              <TransacoesList
                :titulo="null"
                :transacoes="transacoesRecebidas"
                :carregando="carregandoTransacoes"
                :mostrar-botao-ver-mais="false"
                :mostrar-colunas="['hash', 'blockNumber', 'timestamp', 'from', 'direction', 'value', 'status']"
                :items-por-pagina="10"
                :esconder-paginacao="false"
              />
            </v-window-item>
          </v-window>
        </v-card-text>
      </v-card>
    </template>
    
    <v-alert
      v-else
      type="error"
      class="mt-4"
    >
      Conta com endereço {{ address }} não encontrada ou ocorreu um erro ao carregar os dados.
    </v-alert>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import { formatarHash, formatarData, formatarNumeroGrande, formatarValorToken } from '@/services/utilidades'
import InfoItem from '@/components/common/InfoItem.vue'
import TransacoesList from '@/components/transactions/TransacoesList.vue'

export default {
  name: 'AccountDetailsView',
  components: {
    InfoItem,
    TransacoesList
  },
  props: {
    address: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      carregando: true,
      carregandoTransacoes: true,
      conta: null,
      transacoes: [],
      tabTransacoes: 'todas',
      enderecoCopiado: false,
      cabecalhosTokens: [
        { title: 'Token', key: 'token', align: 'start' },
        { title: 'Saldo', key: 'saldo', align: 'end' },
        { title: 'Valor', key: 'valor', align: 'end' },
        { title: 'Contrato', key: 'contrato', align: 'start' },
        { title: '', key: 'acoes', align: 'end', sortable: false }
      ]
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    },
    
    transacoesFiltradas() {
      return this.transacoes
    },
    
    transacoesEnviadas() {
      return this.transacoes.filter(tx => tx.from.toLowerCase() === this.address.toLowerCase())
    },
    
    transacoesRecebidas() {
      return this.transacoes.filter(tx => tx.to.toLowerCase() === this.address.toLowerCase())
    },
    
    valorFiatKinX() {
      if (!this.conta || !this.conta.saldo) return null
      // DevNet: no fiat conversion — will come from price oracle in production
      return null
    }
  },
  methods: {
    formatarHash,
    formatarData,
    formatarNumeroGrande,
    formatarValorToken,
    
    formatEndereco(endereco) {
      if (!endereco || typeof endereco !== 'string') return ''
      
      // Pega as primeiras duas letras do endereço após o 0x
      return endereco.substring(2, 4).toUpperCase()
    },
    
    copiarEndereco() {
      navigator.clipboard.writeText(this.address)
        .then(() => {
          this.enderecoCopiado = true
          setTimeout(() => {
            this.enderecoCopiado = false
          }, 2000)
        })
        .catch(err => {
          console.error('Erro ao copiar endereço:', err)
        })
    },
    
    async carregarConta() {
      this.carregando = true
      
      try {
                const { ExplorerAPI } = await import('@/services/api')
        
        // Buscar saldo real da conta
        let balanceData = null
        try {
          balanceData = await ExplorerAPI.contas.obterPorEndereco(this.address)
        } catch (_) {
          // Conta pode não ter saldo mas ainda existir como validador
          balanceData = { balance: 0 }
        }
        
        // Verificar se é validador
        let isValidador = false
        let validadorInfo = null
        try {
          const validadores = await ExplorerAPI.validadores.obterTodos()
          validadorInfo = validadores.find(v => 
            (v.endereco || v.address || '').toLowerCase().includes(this.address.toLowerCase().substring(0, 16)) ||
            this.address.toLowerCase().includes((v.endereco || v.address || '').toLowerCase())
          )
          isValidador = !!validadorInfo
        } catch (_) {}
        
        // Verificar se é contrato
        let isContrato = false
        let contratoInfo = null
        try {
          const contrato = await ExplorerAPI.contratos.obterPorEndereco(this.address)
          if (contrato && !contrato.error) {
            isContrato = true
            contratoInfo = contrato
          }
        } catch (_) {}
        
        this.conta = {
          endereco: this.address,
          saldo: balanceData?.balance || balanceData?.saldo || 0,
          numTransacoes: balanceData?.transactionCount || 0,
          isContrato,
          isValidador,
          tokens: balanceData?.tokens || []
        }
        
        // Para validadores
        if (isValidador && validadorInfo) {
          this.conta.stake = validadorInfo.stake || 0
          this.conta.merit = validadorInfo.merit || 0
          this.conta.posemScore = validadorInfo.posemScore || 0
          this.conta.blocosPropostos = validadorInfo.blocksProposed || validadorInfo.blocosCriados || 0
          this.conta.uptime = validadorInfo.uptime || 0
          this.conta.ultimoBloco = validadorInfo.lastActive ? new Date(validadorInfo.lastActive) : null
        }
        
        // Para contratos
        if (this.conta.isContrato && contratoInfo) {
          this.conta.criador = contratoInfo.deployer || ''
          this.conta.criadoEm = contratoInfo.deployed_at ? new Date(contratoInfo.deployed_at * 1000) : null
          this.conta.txHash = contratoInfo.code_hash || ''
        }
        
      } catch (error) {
        console.error('Erro ao carregar conta ' + this.address + ':', error)
        this.conta = null
      } finally {
        this.carregando = false
      }
      
      // Carrega transações da conta
      this.carregarTransacoes()
    },
    
    async carregarTransacoes() {
      this.carregandoTransacoes = true
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.contas.obterTransacoes(this.address, 1, 20)
        
        let txList = data
        if (data && data.transactions) txList = data.transactions
        if (!Array.isArray(txList)) txList = []
        
        this.transacoes = txList.map(tx => ({
          hash: tx.hash || '',
          blockNumber: tx.blockNumber || tx.blocoNumero || 0,
          timestamp: tx.timestamp ? new Date(tx.timestamp) : new Date(),
          from: tx.from || tx.de || '',
          to: tx.to || tx.para || '',
          value: tx.value || tx.valor || 0,
          gas: tx.gas || 0,
          gasPrice: tx.gasPrice || tx.taxaGas || 0,
          status: tx.status || 'success',
        }))
      } catch (error) {
        console.error('Erro ao carregar transações da conta ' + this.address + ':', error)
        this.transacoes = []
      } finally {
        this.carregandoTransacoes = false
      }
    }
  },
  async mounted() {
    await this.carregarConta()
  },
  watch: {
    address: {
      immediate: true,
      handler() {
        this.carregarConta()
      }
    }
  }
}
</script>

<style scoped>
.account-details {
  padding-bottom: 32px;
}
</style>
